import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { Row, Col } from 'react-bootstrap';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import * as _ from 'lodash';
import * as Yup from 'yup';
import Button from 'components/inputs/Button';
import TextInput from 'components/inputs/Input';
import PasswordStrengthBar from 'components/inputs/PasswordStrengthBar';
import { CHANGE_EXPIRED_PASSWORD } from 'actions/authAction';
import { toast } from 'react-toastify';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { parsePhoneNumber } from 'react-phone-number-input';
import PhoneNumberInput from 'components/inputs/PhoneNumberInput';
import PasswordRules from 'components/inputs/PasswordRules';

const ChangeExpiredPassword = () => {
  const dispatch = useDispatch();
  const history = useNavigate();
  const { t } = useTranslation();

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const getCountryCode = _.get(parsePhoneNumber(countryCode), 'countryCallingCode', '');
  const getPhoneNumber = _.get(parsePhoneNumber(countryCode), 'nationalNumber', '');

  const resetPassword = useCallback((data) => {
    dispatch({
      type: CHANGE_EXPIRED_PASSWORD.REQUEST,
      payload: { ...data },
      cb: (res) => {
        if (res) {
          if (_.get(res, 'status') === 200) {
            toast.success('Password change successfully');
            history('/login');
          } else {
            setErrorMsg(res.data.message);
          }
        }
      },
    });
  }, []);

  const ResetSchema = Yup.object().shape({
    phone: Yup.string().required(t('changeExpiredPassword.phone')),
    old_password: Yup.string().required(t('changeExpiredPassword.oldPassword')).trim(t('changeExpiredPassword.space')).strict(true),
    new_password: Yup.string()
      .required(t('changeExpiredPassword.newPassword'))
      .matches(/^.{8,}$/, t('Minimum 8 characters'))
      .matches(/[A-Z]/, t('At least one uppercase letter'))
      .matches(/[a-z]/, t('At least one lowercase letter'))
      .matches(/[0-9]/, t('At least one number'))
      .matches(/[@$!%*?&#^()_\-+={}[\]|:;"'<>,./~`]/, t('At least one special character'))
      .trim(t('changeExpiredPassword.space'))
      .strict(true),
    confirm_password: Yup.string()
      .oneOf([Yup.ref(t('changeExpiredPassword.refpassword')), null], t('changeExpiredPassword.passCheck'))
      .required(t('changeExpiredPassword.confirmPassword'))
      .trim(t('changeExpiredPassword.space'))
      .strict(true),
  });

  const [isConfirmPasswordFocused, setIsConfirmPasswordFocused] = useState(false);

  return (
    <main className="forgot-page__main">
      <div className="forgot-bg-block">
        <div className="forgot-content-block">
          <div className="resetPassword-content--inner">
            <div className="title">Create New Password</div>
            <div className="resetPassword-form__block">
              <Formik
                initialValues={{ phone: '', country_code: '', old_password: '', new_password: '', confirm_password: '' }}
                validationSchema={ResetSchema}
                onSubmit={(values, { setSubmitting }) => {
                  const changePasswordValues = {
                    ...values,
                    phone: getPhoneNumber,
                    country_code: getCountryCode,
                  };
                  resetPassword(changePasswordValues);
                  setSubmitting(false);
                }}
              >
                {({ values, errors, touched, handleSubmit, setFieldValue }) => (
                  <Form onSubmit={handleSubmit}>
                    <div className="form-content-block">
                      <Row>
                        <Col lg={12} className="password_div resetpass">
                          <PhoneNumberInput
                            label={t('changeExpiredPassword.phoneNo')}
                            name="phone"
                            value={values.phone}
                            onChange={(val) => {
                              setFieldValue(`phone`, val);
                              setCountryCode(val || '');
                              if (!_.isEmpty(phoneError)) {
                                setPhoneError('');
                              }
                            }}
                            error={errors.phone && touched.phone ? errors.phone : phoneError ? phoneError : errorMsg ? errorMsg : null}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col lg={12} className="password_div resetpass">
                          <TextInput
                            label={t('changeExpiredPassword.oldPasswordLabel')}
                            name="old_password"
                            type={showOldPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            value={values.old_password}
                            onChange={(e) => setFieldValue('old_password', e.target.value)}
                            error={errors.old_password && touched.old_password ? errors.old_password : null}
                          />
                          <div
                            type="button"
                            className="password-toggle-button"
                            onClick={() => setShowOldPassword(!showOldPassword)}
                            style={{
                              cursor: 'pointer',
                              transition: 'color 0.3s ease',
                              color: showOldPassword ? 'white' : '#be210b',
                            }}
                          >
                            {showOldPassword ? <FiEye /> : <FiEyeOff />}
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg={12} className="password_div resetpass">
                          <TextInput
                            label={t('changeExpiredPassword.newPasswordLabel')}
                            name="new_password"
                            type={showNewPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            value={values.new_password}
                            onChange={(e) => setFieldValue('new_password', e.target.value)}
                            error={errors.new_password && touched.new_password ? errors.new_password : null}
                          />
                          <div
                            type="button"
                            className="password-toggle-button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            style={{
                              cursor: 'pointer',
                              transition: 'color 0.3s ease',
                              color: showNewPassword ? 'white' : '#be210b',
                            }}
                          >
                            {showNewPassword ? <FiEye /> : <FiEyeOff />}
                          </div>
                          {values.new_password.length > 0 && (
                            <>
                              <PasswordStrengthBar password={values.new_password} />
                              <PasswordRules password={values.new_password} />
                            </>
                          )}
                        </Col>
                      </Row>
                      <Row>
                        <Col lg={12} className="password_div resetpass">
                          <TextInput
                            label={t('changeExpiredPassword.confirmPasswordLabel')}
                            name="confirm_password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={values.confirm_password}
                            onChange={(e) => {
                              setFieldValue('confirm_password', e.target.value);
                              setPasswordMatch(e.target.value === values.new_password);
                            }}
                            error={errors.confirm_password && touched.confirm_password ? errors.confirm_password : null}
                            style={{
                              border: isConfirmPasswordFocused ? (passwordMatch ? '2px solid #19f600' : '2px solid red') : '2px solid #3b4a56',
                            }}
                            onFocus={() => setIsConfirmPasswordFocused(true)}
                            onBlur={() => setIsConfirmPasswordFocused(false)}
                          />
                          <div
                            type="button"
                            className="password-toggle-button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            style={{
                              cursor: 'pointer',
                              transition: 'color 0.3s ease',
                              color: showConfirmPassword ? 'white' : '#be210b',
                            }}
                          >
                            {showConfirmPassword ? <FiEye /> : <FiEyeOff />}
                          </div>
                          {values.confirm_password.length > 0 && <PasswordStrengthBar password={values.confirm_password} />}
                          <div className="icon-and-message">
                            {values.confirm_password.length > 0 &&
                              isConfirmPasswordFocused && ( // Added isConfirmPasswordFocused check
                                <div className="icon-container">
                                  {passwordMatch ? <FaCheck className="check-icon" /> : <FaTimes className="cross-icon" />}
                                </div>
                              )}
                            {values.confirm_password.length > 0 &&
                              isConfirmPasswordFocused && ( // Added isConfirmPasswordFocused check
                                <div className="message-container">
                                  {passwordMatch ? (
                                    <span className="match-text" style={{ color: '#19f600' }}>
                                      {t('resetPassword.passwordMatch')}
                                    </span>
                                  ) : (
                                    <span className="mismatch-text">{t('resetPassword.passwordNoMatch')}</span>
                                  )}
                                </div>
                              )}
                          </div>
                        </Col>
                      </Row>
                      <div className="form-button--block">
                        <Button type="submit" className="reset--btn">
                          {t('button.resetPassword')}
                        </Button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ChangeExpiredPassword;
